import * as React from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from "@ando/ui";
import type {
  AdminHardDeleteOrgResult,
  AdminOrganization,
} from "@ando/core";
import { hardDeleteAdminOrg } from "@ando/core";

import { adminOrgKeys } from "@/features/admin/hooks/useAdminOrg";

/**
 * HardDeleteOrgDialog — irreversible cascading delete.
 *
 * The user must TYPE the org ``slug`` exactly before the confirm button
 * unlocks (matches v1 admin-v2 behavior). The wire call carries
 * ``{ confirm_migrate_users: true }`` so the BE refuses accidental hits.
 *
 * Outcomes:
 *   - ``status: "deleted"`` → org row gone, every member moved to the
 *     Default Tenant. We navigate back to the org list and toast a
 *     summary.
 *   - ``status: "partial_migration"`` → at least one member could not be
 *     migrated; the BE rolled back the delete (org still exists). We
 *     surface the failed users inline so the admin can resolve them and
 *     retry.
 */

export interface HardDeleteOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  org: AdminOrganization;
}

export function HardDeleteOrgDialog({
  open,
  onOpenChange,
  org,
}: HardDeleteOrgDialogProps): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [slugInput, setSlugInput] = React.useState("");
  const [partialResult, setPartialResult] = React.useState<
    AdminHardDeleteOrgResult | null
  >(null);

  // Reset the typed slug + last partial result whenever the dialog
  // RE-opens. Closing it should leave nothing stale around for next time.
  React.useEffect(() => {
    if (open) {
      setSlugInput("");
      setPartialResult(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => hardDeleteAdminOrg(org.id),
    onSuccess: (result) => {
      if (result.status === "deleted") {
        toast.success(i18n._(msg`Organizzazione eliminata`), {
          description: i18n._(
            msg`${org.name} · ${result.migrated_users.length} membri migrati al Default Tenant.`,
          ),
        });
        // The detail page is now reading a deleted row — bounce to the
        // list and let TanStack invalidate everything org-scoped.
        qc.removeQueries({ queryKey: adminOrgKeys.org(org.id) });
        qc.invalidateQueries({ queryKey: adminOrgKeys.list });
        onOpenChange(false);
        void navigate({ to: "/admin/orgs" });
      } else {
        // Partial migration: BE rolled back the org delete (409 path
        // converted to a non-throwing branch in the API wrapper).
        setPartialResult(result);
        toast.error(i18n._(msg`Migrazione parziale`), {
          description: i18n._(
            msg`${result.failed_users.length} utenti non migrati. Org NON eliminata.`,
          ),
        });
        qc.invalidateQueries({ queryKey: adminOrgKeys.org(org.id) });
        qc.invalidateQueries({ queryKey: adminOrgKeys.members(org.id) });
      }
    },
    onError: (err) => {
      toast.error(i18n._(msg`Eliminazione fallita`), {
        description:
          err instanceof Error ? err.message : i18n._(msg`Errore inatteso.`),
      });
    },
  });

  const slugMatches = slugInput === org.slug;
  const canSubmit = slugMatches && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle size={16} aria-hidden />
            {i18n._(msg`Elimina «${org.name}»?`)}
          </DialogTitle>
          <DialogDescription>
            {i18n._(msg`Operazione`)} <strong>{i18n._(msg`irreversibile`)}</strong>
            {". "}
            {i18n._(
              msg`Tutti i membri vengono migrati al Default Tenant e i dati org-owned vengono eliminati.`,
            )}
          </DialogDescription>
        </DialogHeader>

        {partialResult && partialResult.status === "partial_migration" ? (
          <div
            className="space-y-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-[12.5px] text-ink-1"
            role="alert"
          >
            <div className="font-semibold text-warning-foreground">
              {partialResult.failed_users.length === 1
                ? i18n._(
                    msg`Migrazione parziale (${partialResult.failed_users.length} errore)`,
                  )
                : i18n._(
                    msg`Migrazione parziale (${partialResult.failed_users.length} errori)`,
                  )}
            </div>
            <ul className="space-y-1 font-mono text-[11px] text-ink-2">
              {partialResult.failed_users.slice(0, 6).map((f) => (
                <li key={f.user_id}>
                  {f.user_id} — {f.error}
                </li>
              ))}
              {partialResult.failed_users.length > 6 ? (
                <li className="text-ink-3">
                  {i18n._(msg`…e altri ${partialResult.failed_users.length - 6}`)}
                </li>
              ) : null}
            </ul>
            <p className="text-[11px] text-ink-3">
              {i18n._(
                msg`L'org NON è stata eliminata. Risolvi i conflitti e riprova.`,
              )}
            </p>
          </div>
        ) : null}

        <div className="space-y-2">
          <label
            htmlFor="hard-delete-slug-input"
            className="block text-[12.5px] text-ink-1"
          >
            {i18n._(msg`Digita lo slug`)}{" "}
            <code className="rounded bg-destructive/10 px-1.5 py-0.5 font-mono text-[12px] text-destructive">
              {org.slug}
            </code>{" "}
            {i18n._(msg`per confermare:`)}
          </label>
          <input
            id="hard-delete-slug-input"
            value={slugInput}
            onChange={(e) => setSlugInput(e.target.value)}
            placeholder={org.slug}
            autoComplete="off"
            spellCheck={false}
            className="h-9 w-full rounded-md border border-hairline bg-card px-3 font-mono text-[13px] text-ink-1 focus-visible:border-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40"
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {i18n._(msg`Annulla`)}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
          >
            {mutation.isPending ? (
              <Loader2 size={14} className="mr-2 animate-spin" aria-hidden />
            ) : null}
            {i18n._(msg`Elimina definitivamente`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
